/**
 * 이상한 방 - 꾸밈의 **모양 표** (id → CSS 값)
 *
 * 근거 문서: 로드맵 확정안 3.2절(꾸밈은 `--gh-*` 덮어쓰기로만 적용) · `game/economy/looks.ts` 머리말
 *
 * ## 왜 `cosmeticVars.ts`에서 갈라졌나
 *
 * 규칙(무엇을 내보낼 수 있는가 · 무엇은 절대 못 내보내는가 · 어떻게 심는가)과 데이터(어떤 id가 어떤 값인가)는
 * 고치는 이유가 다르다. 꾸밈이 스물셋일 때는 한 파일이었지만 백여든이 되면서 규칙 예순 줄이 표 삼백 줄에
 * 묻혔다 - 규칙을 읽으러 온 사람이 표를 스크롤해야 하면 그 규칙은 없는 것과 같다.
 *
 * **그래서 이 파일에는 규칙이 없다.** 허용 목록(`COSMETIC_VARS`)도 금지 목록(`PROTECTED_VARS`)도
 * 심는 함수도 전부 `cosmeticVars.ts`에 있고, 여기 있는 값이 그 규칙을 지키는지는
 * `visualRules.test.ts`가 표 전체를 훑어 검사한다(반경은 CSS의 허용 집합과 같은 집합, 보호 토큰은 0건).
 *
 * ## 값이 규칙을 지키는 자리 셋 (실측으로 알아낸 것들)
 *
 * **결과 테두리는 `--gh-fg`나 `--gh-accent-shade`만 쓴다.** `--gh-panel-soft`는 `rgba(25,21,18,0.55)`이고
 * 결과 화면 바탕은 같은 먹색의 0.96이라(`gameHud.css`의 `.rs-root`), 그 색으로 그은 테두리는 **화면에
 * 나타나지 않는다.** 끼워도 아무것도 안 바뀌는 꾸밈은 3.2절이 금지한 "가치 0인 봉투"인데, 변수를 하나
 * 내보내기는 하므로 `visualRules.test.ts`의 "실제로 무언가를 낸다" 검사를 **통과하면서** 값이 0이었다.
 * 색을 둘로 닫아 그 구멍을 막았고, 그 사실을 테스트가 소스에서 검사한다.
 *
 * **액센트 노랑을 넓게 까는 봉투 겉면은 SSR뿐이다**(금박과 빛살 둘). SR이 셋 더 쓰면 "노란 봉투가 왔다"가
 * 더 이상 신호가 아니게 된다 - 확률이 아니라 인상으로 등급을 흐리는 자리다.
 *
 * **표식 글리프는 반각이어야 한다.** 34px 고리 안에 15px로 앉으므로 전각(〇 같은 것)은 넘치고, 기본이
 * 이모지 표현인 문자는 색깔 그림으로 그려진다. 예순한 자 전부를 브라우저 canvas로 재서 두부·색·크기를
 * 확인했다(로드맵 7절 P8).
 *
 * ## 모르는 id는 없는 것이다
 *
 * 네 표 모두 `Record<string, ...>`이라 저장값이 앞선 판의 id를 들고 와도 조용히 넘어간다
 * (`cosmeticVarsFor`). 꾸밈 하나 때문에 화면이 서지 않는 것이 이 기능이 감당할 실패의 상한이다.
 */

/* --- 표식 -------------------------------------------------------------------- */

/**
 * 표식 하나의 모양. `glyph`가 빈 문자열이면 기본(빈 고리)이다.
 *
 * 글자로 그리는 이유는 고리를 도형으로 깎으면(`clip-path`) 테두리가 잘려 나가 판정색(맞음·빗나감)이
 * 사라지기 때문이다. 고리는 그대로 두고 그 안에 글자를 한 자 찍으면 색 규약은 유지되고 모양만 사람마다 달라진다.
 */
type MarkerStyle = Readonly<{ glyph: string; radius?: string; anim?: string }>

/** 애니메이션 값 둘. 키프레임 이름의 원본은 `gameHud.css`다(여기서 만드는 것은 짧은 지시문뿐이다). */
const MARKER_PULSE = 'gh-mark-glyph-pulse 900ms ease-in-out infinite'
const MARKER_SPIN = 'gh-mark-glyph-spin 1400ms linear infinite'

/**
 * 표식 여덟. 기본(`markerPin`)만 빈 값이고, 그래서 아무것도 안 뽑은 사람의 표식은 지금 그대로다.
 * 반경을 바꾸는 것은 네모난 두 자리뿐이고 값은 CSS의 허용 집합 안에 있다(머리말).
 *
 * 완주 보상(`markerGlint`)은 **새 키프레임을 만들지 않는다** - 반짝임은 이미 있는 두근거림
 * (`gh-mark-glyph-pulse`: 불투명도 + 크기)이 그대로 읽히는 움직임이라, 이름만 다른 키프레임을 하나 더
 * 두면 언젠가 둘 중 하나만 고쳐진다. 다른 것은 글자 하나뿐이다(✧).
 */
export const MARKER_STYLES: Readonly<Record<string, MarkerStyle>> = {
  markerPin: { glyph: '' },
  markerCircle: { glyph: '"\\25CF"' },
  markerCross: { glyph: '"\\2715"', radius: 'var(--gh-radius-plate)' },
  markerTriangle: { glyph: '"\\25B2"', radius: 'var(--gh-radius-plate)' },
  markerStar: { glyph: '"\\2605"' },
  markerPulse: { glyph: '"\\25C9"', anim: MARKER_PULSE },
  markerSpin: { glyph: '"\\2726"', anim: MARKER_SPIN },
  markerGlint: { glyph: '"\\2727"', anim: MARKER_PULSE },
  markerSquare: { glyph: '"\\25A0"', radius: 'var(--gh-radius-plate)' },
  markerBlank: { glyph: '"\\25A1"', radius: 'var(--gh-radius-plate)' },
  markerNestedSquare: { glyph: '"\\25A3"', radius: 'var(--gh-radius-plate)' },
  markerRuled: { glyph: '"\\25A4"', radius: 'var(--gh-radius-plate)' },
  markerLattice: { glyph: '"\\25A6"', radius: 'var(--gh-radius-plate)' },
  markerHatch: { glyph: '"\\25A7"', radius: 'var(--gh-radius-plate)' },
  markerShade: { glyph: '"\\25E7"', radius: 'var(--gh-radius-plate)' },
  markerDogEar: { glyph: '"\\25EA"', radius: 'var(--gh-radius-plate)' },
  markerPunched: { glyph: '"\\22A1"', radius: 'var(--gh-radius-plate)' },
  markerCornerFill: { glyph: '"\\25E2"', radius: 'var(--gh-radius-plate)' },
  markerDiamond: { glyph: '"\\25C6"' },
  markerNestedDiamond: { glyph: '"\\25C8"' },
  markerBullseye: { glyph: '"\\25CE"' },
  markerRing: { glyph: '"\\25CB"' },
  markerHalfMoon: { glyph: '"\\25D0"' },
  markerMissingPiece: { glyph: '"\\25D5"' },
  markerWedge: { glyph: '"\\25BC"' },
  markerCone: { glyph: '"\\25B3"' },
  markerStarSix: { glyph: '"\\2736"' },
  markerStarEight: { glyph: '"\\2737"' },
  markerStarSolid: { glyph: '"\\2738"' },
  markerStarTwelve: { glyph: '"\\2739"' },
  markerPinwheel: { glyph: '"\\2735"' },
  markerStarRinged: { glyph: '"\\2742"' },
  markerStarTeardrop: { glyph: '"\\273B"' },
  markerStarHollow: { glyph: '"\\273C"' },
  markerStarPlump: { glyph: '"\\273D"' },
  markerStarBead: { glyph: '"\\2749"' },
  markerWhirl: { glyph: '"\\2743"' },
  markerBlade: { glyph: '"\\274B"' },
  markerFlowerSix: { glyph: '"\\273E"' },
  markerFlower: { glyph: '"\\273F"' },
  markerFlowerWhite: { glyph: '"\\2740"' },
  markerFlowerEight: { glyph: '"\\2741"' },
  markerSnowflake: { glyph: '"\\2745"' },
  markerFrostFlake: { glyph: '"\\2746"' },
  markerArrowUp: { glyph: '"\\21E7"' },
  markerArrowDown: { glyph: '"\\21E9"' },
  markerArrowRewind: { glyph: '"\\21BA"' },
  markerArrowSwap: { glyph: '"\\21C4"' },
  markerArrowDouble: { glyph: '"\\21D2"' },
  markerInfinity: { glyph: '"\\221E"' },
  markerThreeBars: { glyph: '"\\2261"' },
  markerThreeDots: { glyph: '"\\2234"' },
  markerThickPlus: { glyph: '"\\254B"' },
  markerFullBlock: { glyph: '"\\25F0"', radius: 'var(--gh-radius-plate)' },
  markerHalfBlock: { glyph: '"\\25EB"', radius: 'var(--gh-radius-plate)' },
  markerShadeBlock: { glyph: '"\\2592"', radius: 'var(--gh-radius-plate)' },
  markerCog: { glyph: '"\\273A"', anim: MARKER_SPIN },
  markerWheel: { glyph: '"\\2297"', anim: MARKER_SPIN },
  markerCompass: { glyph: '"\\2732"', anim: MARKER_SPIN },
  markerRipple: { glyph: '"\\25CC"', anim: MARKER_PULSE },
  markerBeacon: { glyph: '"\\2731"', anim: MARKER_PULSE },
  markerFloat: { glyph: '"\\274F"', radius: 'var(--gh-radius-plate)', anim: MARKER_PULSE },
}

/* --- 이름표 ------------------------------------------------------------------- */

/**
 * 이름표 색 다섯. 기본(`tagYellow`)은 **값이 없다** - 액센트 노랑을 여기서 다시 적으면 같은 색이 두 곳에 살고,
 * 그중 하나가 언젠가 조금 달라진다. 나머지 넷은 토큰 밖의 색이고 그 근거는 머리말에 있다.
 */
export const TAG_COLORS: Readonly<Record<string, string>> = {
  tagMint: '#7fe3c0',
  tagCoral: '#ff9a7a',
  tagSky: '#8fc7ff',
  tagLilac: '#c6a8ff',
  /* 완주 보상. 다섯 중 어느 것과도 색상환에서 멀고, 무엇보다 **노랑이 아니다** - 액센트에 가까운 색을
     여기 두면 "노란 것이 찾을 것"이 이름표에서 한 번 흔들린다. */
  tagPink: '#ff6fd8',
  tagRose: '#e87d81',
  tagStrawberry: '#de5476',
  tagCherry: '#f36868',
  tagPeach: '#fdb8a5',
  tagApricot: '#f8bb81',
  tagCopper: '#c98669',
  tagIvory: '#ddc6b0',
  tagChartreuse: '#b9f485',
  tagMugwort: '#a2aa88',
  tagSprout: '#b8e297',
  tagMoss: '#87b57d',
  tagGrass: '#71dc60',
  tagPea: '#c9f5cc',
  tagGreen: '#6acd81',
  tagEmerald: '#47f0a4',
  tagCeladon: '#90c1b0',
  tagJade: '#b1f1eb',
  tagTeal: '#5dd9df',
  tagAqua: '#a3e0fa',
  tagOcean: '#57b8db',
  tagAsh: '#a0a5b0',
  tagSilver: '#cbdbe2',
  tagIndigo: '#507ce2',
  tagCornflower: '#88a3f1',
  tagNavy: '#5a58da',
  tagHydrangea: '#c4c4e9',
  tagIris: '#9376d5',
  tagWisteria: '#b29cd3',
  tagViolet: '#b577f8',
  tagGrape: '#be67cb',
  tagAmethyst: '#f09df6',
  tagPlum: '#cb80bd',
  tagPeony: '#f3b9eb',
  tagRouge: '#ca8ca7',
  tagFuchsia: '#df5397',
  tagBlossom: '#ecbbc5',
}

/* --- 결과 테두리 · 봉투 겉면 ------------------------------------------------------ */

/**
 * 결과 카드의 테두리. **토큰 색만 쓴다** - 여기서 새 색을 지어내면 화면의 색 규약이 표에서 새어 나간다.
 * 기본(`frameNone`)은 목록에 없어 아무 값도 내지 않는다(= 지금 화면 그대로).
 *
 * 테두리를 카드 자신이 아니라 덮개(`.rs-card::after`)에 그리는 이유는 안쪽 스크롤 영역의 레이아웃을
 * 밀지 않기 위해서다 - 테두리 하나로 점수표의 줄이 접히면 그건 꾸밈이 아니라 규칙이다.
 */
export const FRAME_STYLES: Readonly<Record<string, string>> = {
  /* 마스킹테이프: 크림색 점선 굵은 띠. */
  frameTape: '5px dashed var(--gh-fg)',
  /* 클립보드: 액센트의 그늘색 판. 노랑 자체를 쓰지 않는 이유는 "노란 것이 찾을 것"과 경쟁하지 않기 위해서다. */
  frameClip: '4px solid var(--gh-accent-shade)',
  /* 완주 보상(액자): 겹줄. `double`은 굵기 하나로 두 줄을 그리므로 새 색도 새 요소도 필요 없다. */
  frameGallery: '6px double var(--gh-fg)',
  frameLabel: '2px solid var(--gh-fg)',
  frameSealTape: '9px solid var(--gh-fg)',
  frameTearLine: '3px dashed var(--gh-fg)',
  framePushpin: '7px dotted var(--gh-fg)',
  frameManuscript: '4px double var(--gh-fg)',
  frameFold: '5px ridge var(--gh-fg)',
  frameGlass: '6px inset var(--gh-fg)',
  frameIndexTab: '4px outset var(--gh-fg)',
  framePencil: '2px solid var(--gh-accent-shade)',
  frameFolder: '8px solid var(--gh-accent-shade)',
  frameRuler: '6px dashed var(--gh-accent-shade)',
  frameFilm: '5px dotted var(--gh-accent-shade)',
  frameSpine: '8px double var(--gh-accent-shade)',
  frameTwine: '6px outset var(--gh-accent-shade)',
  frameDoorJamb: '7px groove var(--gh-accent-shade)',
  frameWindowSill: '6px inset var(--gh-accent-shade)',
  frameGrid: '3px dotted var(--gh-fg)',
  frameMatBoard: '12px solid var(--gh-accent-shade)',
}

/**
 * 봉투 겉면(`.ev-card__front`의 `background-image`). 값은 전부 토큰에서 나온 그러데이션이고 색 리터럴이 없다.
 * 기본(`envelopeKraft`)은 목록에 없어 CSS의 폴백(지금의 크라프트 사선)이 그대로 산다.
 */
export const ENVELOPE_STYLES: Readonly<Record<string, string>> = {
  envelopeStriped: 'repeating-linear-gradient(90deg, var(--gh-panel-soft) 0 7px, transparent 7px 18px)',
  envelopeStamped:
    'radial-gradient(circle at 78% 20%, var(--gh-accent-shade) 0 12px, transparent 13px),' +
    ' repeating-linear-gradient(135deg, var(--gh-panel-soft) 0 2px, transparent 2px 8px)',
  /* SSR. 금박은 액센트에서 그늘까지의 한 줄 그러데이션이다(새 색이 아니라 있는 노랑의 기울기다). */
  envelopeGilt: 'linear-gradient(160deg, var(--gh-accent) 0%, var(--gh-accent-shade) 100%)',
  /* 완주 보상(밀랍): 가운데 봉인 한 덩이 + 성근 사선. 금박과 달리 액센트 자체를 넓게 깔지 않는다. */
  envelopeWax:
    'radial-gradient(circle at 50% 58%, var(--gh-accent-shade) 0 26px, transparent 27px),' +
    ' repeating-linear-gradient(45deg, var(--gh-panel-soft) 0 3px, transparent 3px 11px)',
  envelopeGrid: 
    'repeating-linear-gradient(0deg, var(--gh-panel-soft) 0 1px, transparent 1px 12px),' +
    ' repeating-linear-gradient(90deg, var(--gh-panel-soft) 0 1px, transparent 1px 12px)',
  envelopeLattice: 
    'repeating-linear-gradient(0deg, var(--gh-panel) 0 3px, transparent 3px 20px),' +
    ' repeating-linear-gradient(90deg, var(--gh-panel) 0 3px, transparent 3px 20px)',
  envelopePinstripe: 'repeating-linear-gradient(90deg, var(--gh-panel-soft) 0 1px, transparent 1px 6px)',
  envelopeHatch: 'repeating-linear-gradient(45deg, var(--gh-panel) 0 2px, transparent 2px 6px)',
  envelopeDiamond: 
    'repeating-linear-gradient(45deg, var(--gh-panel-soft) 0 1px, transparent 1px 14px),' +
    ' repeating-linear-gradient(135deg, var(--gh-panel-soft) 0 1px, transparent 1px 14px)',
  envelopeCheck: 
    'repeating-linear-gradient(90deg, var(--gh-panel-soft) 0 10px, transparent 10px 20px),' +
    ' repeating-linear-gradient(0deg, var(--gh-panel) 0 10px, transparent 10px 20px)',
  envelopeRuled: 
    'linear-gradient(90deg, transparent 0 22px, var(--gh-accent-shade) 22px 23px, transparent 23px 100%),' +
    ' repeating-linear-gradient(0deg, var(--gh-panel-soft) 0 1px, transparent 1px 16px)',
  envelopeDoubleRule: 
    'repeating-linear-gradient(0deg, var(--gh-panel-soft) 0 1px, transparent 1px 4px, var(--gh-panel-soft) 4px 5px, transparent 5px 18px)',
  envelopeRipple: 'repeating-radial-gradient(circle at 50% -30%, var(--gh-panel-soft) 0 2px, transparent 2px 14px)',
  envelopeSpeckle: 
    'radial-gradient(circle 3px at 11% 8%, var(--gh-panel-soft) 0 60%, transparent 100%),' +
    ' radial-gradient(circle 3px at 34% 17%, var(--gh-panel-soft) 0 60%, transparent 100%),' +
    ' radial-gradient(circle 3px at 58% 6%, var(--gh-panel-soft) 0 60%, transparent 100%),' +
    ' radial-gradient(circle 3px at 83% 15%, var(--gh-panel-soft) 0 60%, transparent 100%),' +
    ' radial-gradient(circle 3px at 19% 33%, var(--gh-panel-soft) 0 60%, transparent 100%),' +
    ' radial-gradient(circle 3px at 45% 29%, var(--gh-panel-soft) 0 60%, transparent 100%),' +
    ' radial-gradient(circle 3px at 71% 38%, var(--gh-panel-soft) 0 60%, transparent 100%),' +
    ' radial-gradient(circle 3px at 92% 47%, var(--gh-panel-soft) 0 60%, transparent 100%),' +
    ' radial-gradient(circle 3px at 9% 55%, var(--gh-panel-soft) 0 60%, transparent 100%),' +
    ' radial-gradient(circle 3px at 37% 61%, var(--gh-panel-soft) 0 60%, transparent 100%),' +
    ' radial-gradient(circle 3px at 63% 68%, var(--gh-panel-soft) 0 60%, transparent 100%),' +
    ' radial-gradient(circle 3px at 24% 79%, var(--gh-panel-soft) 0 60%, transparent 100%),' +
    ' radial-gradient(circle 3px at 51% 88%, var(--gh-panel-soft) 0 60%, transparent 100%),' +
    ' radial-gradient(circle 3px at 79% 84%, var(--gh-panel-soft) 0 60%, transparent 100%)',
  envelopeWoodGrain: 
    'repeating-radial-gradient(ellipse 20px 240px at 32% 50%, var(--gh-panel-soft) 0 1px, transparent 1px 9px),' +
    ' repeating-linear-gradient(90deg, var(--gh-panel) 0 1px, transparent 1px 17px)',
  envelopeCorrugated: 'repeating-linear-gradient(90deg, var(--gh-panel) 0px, transparent 4px, var(--gh-panel) 8px)',
  envelopeWideBand: 'repeating-linear-gradient(0deg, var(--gh-panel) 0 10px, transparent 10px 26px)',
  envelopeHairline: 'repeating-linear-gradient(0deg, var(--gh-panel-soft) 0 1px, transparent 1px 5px)',
  envelopeAir: 
    'linear-gradient(to bottom, transparent 0 16px, var(--gh-ink) 16px calc(100% - 16px), transparent calc(100% - 16px)),' +
    ' repeating-linear-gradient(60deg, var(--gh-fg) 0 6px, transparent 6px 13px, var(--gh-accent-shade) 13px 19px, transparent 19px 26px)',
  envelopeWindow: 
    'linear-gradient(to bottom, var(--gh-ink) 0 58%, var(--gh-fg) 58% 60%, transparent 60% 86%, var(--gh-fg) 86% 88%, var(--gh-ink) 88%),' +
    ' linear-gradient(to right, var(--gh-ink) 0 20px, var(--gh-fg) 20px 22px, transparent 22px calc(100% - 22px), var(--gh-fg) calc(100% - 22px) calc(100% - 20px), var(--gh-ink) calc(100% - 20px))',
  envelopePostmark: 
    'radial-gradient(circle at 30% 26%, transparent 0 15px, var(--gh-fg) 15px 17px, transparent 17px 21px, var(--gh-fg) 21px 22px, transparent 22px),' +
    ' linear-gradient(to right, var(--gh-ink) 0 46%, transparent 46%),' +
    ' repeating-linear-gradient(4deg, var(--gh-accent-shade) 0 2px, transparent 2px 15px)',
  envelopePerf: 
    'linear-gradient(to bottom, transparent 0 10px, var(--gh-fg) 10px 11px, var(--gh-ink) 11px calc(100% - 11px), var(--gh-fg) calc(100% - 11px) calc(100% - 10px), transparent calc(100% - 10px)),' +
    ' repeating-linear-gradient(90deg, var(--gh-fg) 0 7px, transparent 7px 15px)',
  envelopeCrease: 
    'linear-gradient(to bottom, transparent 0 33%, var(--gh-fg) 33% calc(33% + 1px), var(--gh-panel-soft) calc(33% + 1px) calc(33% + 5px), transparent calc(33% + 5px) 66%, var(--gh-fg) 66% calc(66% + 1px), var(--gh-panel-soft) calc(66% + 1px) calc(66% + 5px), transparent calc(66% + 5px)),' +
    ' repeating-linear-gradient(100deg, var(--gh-panel-soft) 0 1px, transparent 1px 15px)',
  envelopeDoubleSeal: 
    'conic-gradient(at 50% 58%, transparent 0 44deg, var(--gh-fg) 44deg 45deg, transparent 45deg 315deg, var(--gh-fg) 315deg 316deg, transparent 316deg),' +
    ' conic-gradient(at 50% 76%, transparent 0 44deg, var(--gh-accent-shade) 44deg 46deg, transparent 46deg 314deg, var(--gh-accent-shade) 314deg 316deg, transparent 316deg),' +
    ' linear-gradient(to bottom, var(--gh-panel-soft) 0 40%, transparent 78%)',
  envelopeSilver: 
    'linear-gradient(104deg, transparent 0 12%, var(--gh-fg) 17% 20%, transparent 25% 36%, var(--gh-fg) 40% 42%, transparent 47% 63%, var(--gh-fg) 68% 71%, transparent 76% 88%, var(--gh-fg) 92% 94%, transparent 98%),' +
    ' repeating-linear-gradient(104deg, var(--gh-panel-soft) 0 2px, transparent 2px 7px)',
  envelopeCopper: 
    'repeating-linear-gradient(72deg, var(--gh-accent-shade) 0 6px, transparent 6px 14px, var(--gh-accent-shade) 14px 15px, transparent 15px 28px),' +
    ' linear-gradient(200deg, var(--gh-accent-shade) 0 30%, transparent 64%)',
  envelopeStain: 
    'radial-gradient(ellipse 62px 46px at 20% 76%, var(--gh-accent-shade) 0 14%, transparent 72%),' +
    ' radial-gradient(ellipse 46px 38px at 78% 28%, var(--gh-accent-shade) 0 12%, transparent 74%),' +
    ' radial-gradient(circle 34px at 56% 58%, var(--gh-panel-soft) 0 18%, transparent 78%),' +
    ' radial-gradient(ellipse 120px 78px at 50% 104%, var(--gh-accent-shade) 0 8%, transparent 76%),' +
    ' radial-gradient(ellipse 150px 190px at 50% 48%, transparent 0 42%, var(--gh-panel-soft) 100%)',
  envelopeSunburst: 
    'radial-gradient(circle at 50% 44%, var(--gh-accent) 0 9px, var(--gh-accent-shade) 9px 13px, transparent 14px 24px, var(--gh-fg) 24px 25px, transparent 25px),' +
    ' conic-gradient(from 0deg at 50% 44%, var(--gh-accent-shade) 0 14deg, transparent 14deg 45deg, var(--gh-accent-shade) 45deg 59deg, transparent 59deg 90deg, var(--gh-accent-shade) 90deg 104deg, transparent 104deg 135deg, var(--gh-accent-shade) 135deg 149deg, transparent 149deg 180deg, var(--gh-accent-shade) 180deg 194deg, transparent 194deg 225deg, var(--gh-accent-shade) 225deg 239deg, transparent 239deg 270deg, var(--gh-accent-shade) 270deg 284deg, transparent 284deg 315deg, var(--gh-accent-shade) 315deg 329deg, transparent 329deg 360deg),' +
    ' radial-gradient(circle at 50% 44%, transparent 0 60%, var(--gh-panel-soft) 100%)',
  envelopeDiagonal: 
    'linear-gradient(28deg, transparent 0 49%, var(--gh-fg) 49% 50%, transparent 50%),' +
    ' linear-gradient(28deg, var(--gh-accent-shade) 0 8%, transparent 46% 54%, var(--gh-panel-soft) 100%)',
  envelopeDogEar: 
    'linear-gradient(225deg, var(--gh-fg) 0 34px, var(--gh-accent-shade) 34px 38px, transparent 38px),' +
    ' linear-gradient(45deg, var(--gh-accent-shade) 0 24px, var(--gh-panel-soft) 24px 27px, transparent 27px),' +
    ' repeating-linear-gradient(155deg, var(--gh-panel-soft) 0 1px, transparent 1px 11px)',
  envelopeRegistered: 
    'linear-gradient(to bottom, var(--gh-fg) 0 3px, transparent 3px calc(100% - 3px), var(--gh-fg) calc(100% - 3px)),' +
    ' linear-gradient(to right, var(--gh-fg) 0 3px, transparent 3px calc(100% - 3px), var(--gh-fg) calc(100% - 3px)),' +
    ' linear-gradient(to bottom, transparent 0 9px, var(--gh-accent-shade) 9px 11px, transparent 11px calc(100% - 11px), var(--gh-accent-shade) calc(100% - 11px) calc(100% - 9px), transparent calc(100% - 9px)),' +
    ' linear-gradient(to right, transparent 0 9px, var(--gh-accent-shade) 9px 11px, transparent 11px calc(100% - 11px), var(--gh-accent-shade) calc(100% - 11px) calc(100% - 9px), transparent calc(100% - 9px)),' +
    ' linear-gradient(118deg, transparent 0 40%, var(--gh-accent-shade) 40% 52%, transparent 52%)',
}
